import { useEffect, useState, useMemo } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Star, Eye, EyeSlash, Trash, Check, ChatCircleText } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "published", label: "Published" },
  { id: "hidden", label: "Hidden" },
];

const STATUS_BADGE = {
  published: "border-[#22c55e]/40 text-[#22c55e]",
  pending: "border-[#FF2D2D] text-[#FF2D2D]",
  hidden: "border-[#404040] text-[#a3a3a3]",
};

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reviews");
      setReviews(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const moderate = async (id, status) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { status });
      toast.success(`Review ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const hardDelete = async (id) => {
    if (!window.confirm("Permanently delete this review? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const filtered = useMemo(
    () => (tab === "all" ? reviews : reviews.filter((r) => r.status === tab)),
    [reviews, tab]
  );

  const counts = useMemo(() => ({
    all: reviews.length,
    pending: reviews.filter((r) => r.status === "pending").length,
    published: reviews.filter((r) => r.status === "published").length,
    hidden: reviews.filter((r) => r.status === "hidden").length,
  }), [reviews]);

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Reviews</div>
        <h1 className="font-display text-5xl mt-2">Moderation</h1>
        <p className="text-[#a3a3a3] mt-2 text-sm max-w-xl">Approve client reviews to publish them on the homepage carousel. Hide or delete anything off-message.</p>
      </div>

      {/* Tab filter */}
      <div className="flex gap-1 mb-5 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`reviews-tab-${t.id}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors flex items-center gap-2 ${
              tab === t.id
                ? "border-[#FF2D2D] bg-[#FF2D2D] text-white"
                : "border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D]"
            }`}
          >
            <span>{t.label}</span>
            <span className="text-[10px] tabular-nums">{counts[t.id]}</span>
          </button>
        ))}
      </div>

      <div className="border border-[#262626] divide-y divide-[#262626]" data-testid="reviews-list">
        {loading ? (
          <div className="bg-[#0a0a0a] p-12 text-center text-[#a3a3a3]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-[#0a0a0a] p-12 text-center text-[#a3a3a3]">
            <ChatCircleText size={28} className="mx-auto text-[#262626]" weight="fill" />
            <div className="mt-3 text-sm">{reviews.length === 0 ? "No reviews submitted yet." : "No reviews in this view."}</div>
          </div>
        ) : (
          filtered.map((r) => (
            <div key={r.id} className="bg-[#0a0a0a] p-5" data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={14} weight="fill" className={n <= r.rating ? "text-[#FF2D2D]" : "text-[#262626]"} />
                      ))}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${STATUS_BADGE[r.status]}`}>{r.status}</span>
                    {r.service_type && <span className="text-[10px] text-[#a3a3a3] uppercase tracking-wider">{r.service_type.replace(/_/g, " ")}</span>}
                  </div>
                  <div className="text-sm leading-relaxed mb-3 italic">&ldquo;{r.body}&rdquo;</div>
                  <div className="text-xs text-[#a3a3a3]">
                    <strong className="text-white">{r.display_company || r.client_name}</strong>
                    {r.client_email && <span> · {r.client_email}</span>}
                    {r.project_title && <span> · {r.project_title}</span>}
                    <span className="text-[#666]"> · {new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {r.status !== "published" && (
                    <button
                      onClick={() => moderate(r.id, "published")}
                      data-testid={`publish-${r.id}`}
                      className="btn-primary px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-1.5"
                    >
                      <Check size={11} weight="bold" /> Publish
                    </button>
                  )}
                  {r.status === "published" && (
                    <button
                      onClick={() => moderate(r.id, "hidden")}
                      data-testid={`hide-${r.id}`}
                      className="btn-outline px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-1.5"
                    >
                      <EyeSlash size={11} /> Hide
                    </button>
                  )}
                  {r.status === "hidden" && (
                    <button
                      onClick={() => moderate(r.id, "pending")}
                      data-testid={`unhide-${r.id}`}
                      className="btn-outline px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-1.5"
                    >
                      <Eye size={11} /> Restore
                    </button>
                  )}
                  <button
                    onClick={() => hardDelete(r.id)}
                    data-testid={`delete-review-${r.id}`}
                    title="Delete review"
                    className="px-2 py-1.5 text-xs border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                  >
                    <Trash size={11} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </AdminLayout>
  );
}
